import { Box, InputAdornment, styled, TextField } from '@mui/material'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'

export const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 2,
})

export const SectionHeaderRule = styled(Box)(({ theme }) => ({
  height: 1,
  flex: 1,
  background: GetRGBA(getThemeColor(theme, MUIThemeColors.secondaryLight), 0.6),
}))

export const PickerContainer = styled(Box)({
  position: 'relative',
  marginBottom: 10,
})

export const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 46,
    borderRadius: 10,
    background: getThemeColor(theme, MUIThemeColors.backgroundMain),
    '& fieldset': {
      borderColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
    },
    '&.Mui-focused': {
      background: getThemeColor(theme, MUIThemeColors.white),
    },
    '&.Mui-focused fieldset': {
      borderColor: getThemeColor(theme, MUIThemeColors.primaryMain),
      borderWidth: 1.5,
    },
  },
}))

export const SearchAdornment = styled(InputAdornment)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

export const DropdownPanel = styled(Box)(({ theme }) => ({
  marginTop: 6,
  background: getThemeColor(theme, MUIThemeColors.white),
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: 12,
  boxShadow: `0 16px 34px ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.16)}`,
  overflow: 'hidden',
}))

export const DropdownHeader = styled(Box)(({ theme }) => ({
  padding: '9px 14px',
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.backgroundMain)}`,
}))

export const DropdownList = styled(Box)({
  maxHeight: 262,
  overflowY: 'auto',
  padding: 5,
})

export const DropdownOption = styled('button')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  padding: 10,
  border: 'none',
  background: 'transparent',
  borderRadius: 8,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  '&:hover': {
    background: getThemeColor(theme, MUIThemeColors.backgroundMain),
  },
}))

export const DropdownOptionAdd = styled(Box)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
}))

export const DropdownEmpty = styled(Box)(({ theme }) => ({
  padding: '16px 12px',
  textAlign: 'center',
  fontSize: 13,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

export const SelectedPanel = styled(Box)(({ theme }) => ({
  border: `1.5px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: 14,
  background: getThemeColor(theme, MUIThemeColors.backgroundMain),
  overflow: 'hidden',
}))

export const SelectedPanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  background: getThemeColor(theme, MUIThemeColors.white),
}))

export const CountChip = styled(Box)(({ theme }) => ({
  minWidth: 22,
  height: 22,
  padding: '0 7px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  fontSize: 12,
  fontWeight: 700,
  borderRadius: 11,
}))

export const DragHint = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

export const RowsContainer = styled(Box)({
  maxHeight: 320,
  overflowY: 'auto',
  padding: 10,
})

export const FieldRow = styled(Box)<{ isDragging?: boolean }>(({ theme, isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  padding: '8px 11px',
  marginBottom: 7,
  background: getThemeColor(theme, MUIThemeColors.white),
  border: `1px solid ${
    isDragging ? getThemeColor(theme, MUIThemeColors.primaryMain) : getThemeColor(theme, MUIThemeColors.secondaryLight)
  }`,
  borderRadius: 10,
  boxShadow: isDragging ? `0 10px 22px ${GetRGBA(getThemeColor(theme, MUIThemeColors.primaryMain), 0.22)}` : 'none',
  opacity: isDragging ? 0.55 : 1,
  transition: 'box-shadow .12s, border-color .12s',
}))

export const GripHandle = styled(Box)<{ isDragging?: boolean; isLocked?: boolean }>(
  ({ theme, isDragging, isLocked }) => ({
    flex: 'none',
    width: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isDragging
      ? getThemeColor(theme, MUIThemeColors.primaryMain)
      : getThemeColor(theme, isLocked ? MUIThemeColors.secondaryLight : MUIThemeColors.secondaryDark),
    cursor: isLocked ? 'default' : 'grab',
    opacity: isLocked ? 0.4 : 1,
  }),
)

export const PositionLabel = styled(Box)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  minWidth: 20,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}))

export const FieldName = styled(Box)(({ theme }) => ({
  flex: 1,
  fontSize: 14.5,
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.black),
}))

export const TypeLabel = styled(Box)(({ theme }) => ({
  fontSize: 11.5,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
  fontWeight: 500,
}))

export const EmptyState = styled(Box)({
  padding: '40px 20px',
  textAlign: 'center',
})

export const EndDropZone = styled(Box)({
  minHeight: 22,
})

export const EmptyStateTitle = styled(Box)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))

export const EmptyStateSubtitle = styled(Box)(({ theme }) => ({
  fontSize: 13,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  marginTop: 3,
}))
